import React, { useState, useCallback, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { useRecoilState } from 'recoil';
import PropTypes from 'prop-types';
import { Button } from '@gsa/afp-component-library';
import SelectMakeModelPanel from '../make-model/components/make-model-select-panel';
import CommentSection from '../make-model/make-model-modal-comment-section';
import { useMakeModelProvider } from '../Providers/make-model-panel-provider';
import {
  getMakeModelSuccessMessage,
  getMakeModelErrorMessage,
} from '../components/make-model-approval-helper';
import MakeModelSummary from '../components/make-model-summary';
import {
  makeModelStatusMessageAtom,
  makeModelEditedAtom,
} from '../atoms/make-model-atoms';
import { EDIT_MAKE_MODEL } from '../queries.gql';

const MakeModelEditContent = ({ data, onClose, refetchData }) => {
  const { make, modelCode, modelName, nhtsaModelCode } = data;
  const [commentBody, setCommentBody] = useState('');
  const [commentError, setCommentError] = useState('');
  const [, setToaster] = useRecoilState(makeModelStatusMessageAtom);
  const [, setLastEditedMakeModel] = useRecoilState(makeModelEditedAtom);
  // Captured before the mutation fires so onCompleted can publish exactly
  // which make/model was renamed.
  const lastEditRef = useRef(null);

  const {
    getErrors,
    bodyMake,
    bodyModel,
    bodyMakeInputValue,
    bodyModelInputValue,
    setDirtyFields,
  } = useMakeModelProvider();

  const modalSubTitle = 'Are you sure you want to edit this make and model?';
  const requiredMsg = 'This is a required field';

  const [editMakeAndModel, { loading }] = useMutation(EDIT_MAKE_MODEL, {
    onCompleted: () => {
      onClose();
      setToaster({
        type: 'success',
        message: getMakeModelSuccessMessage(
          'Approve',
          bodyMakeInputValue,
          bodyModelInputValue,
        ),
      });
      // Tell the filter panel the make/model changed so an applied filter
      // still showing the old name refreshes along with the table.
      if (lastEditRef.current) {
        setLastEditedMakeModel(lastEditRef.current);
      }
      refetchData();
    },
    onError: (err) => {
      onClose();
      setToaster({
        type: 'error',
        message: getMakeModelErrorMessage(
          err.message,
          'Approve',
          bodyMakeInputValue,
          bodyModelInputValue,
        ),
      });
    },
  });

  const onCommentChange = (value) => {
    setCommentBody(value);
    setCommentError(value?.trim() ? '' : requiredMsg);
  };

  const handleSave = useCallback(async () => {
    // enable error message for all input fields
    setDirtyFields(['bodyMake', 'bodyModel']);

    // Validate comment
    if (!commentBody?.trim()) {
      setCommentError(requiredMsg);
      return;
    }

    const errors = getErrors();
    if (errors?.length) return;

    const formData = {
      existingValue: {
        makeName: make.makeName,
        makeCode: make.makeCode && Number(make.makeCode),
        modelName: modelName,
        modelCode: modelCode,
      },
      newValue: {
        makeName: bodyMakeInputValue?.trim(),
        makeCode: bodyMake?.value && Number(bodyMake.value),
        modelName: bodyModelInputValue?.trim(),
        modelCode: bodyModel?.value,
      },
      comment: commentBody.trim(),
    };

    // if existingValue is the same as newValue, return early
    if (
      JSON.stringify(formData?.existingValue?.makeName) ===
        JSON.stringify(formData?.newValue?.makeName) &&
      JSON.stringify(formData?.existingValue?.modelName) ===
        JSON.stringify(formData?.newValue?.modelName)
    ) {
      return;
    }
    // otherwise, proceed with the mutation
    lastEditRef.current = {
      previous: formData.existingValue,
      updated: formData.newValue,
    };
    editMakeAndModel({
      variables: {
        editMakeModelInput: formData,
      },
    });
  }, [
    getErrors,
    bodyMake,
    bodyModel,
    bodyMakeInputValue,
    bodyModelInputValue,
    commentBody,
    make,
    modelName,
    modelCode,
    setToaster,
    setLastEditedMakeModel,
    refetchData,
    onClose,
    editMakeAndModel,
    setCommentError,
  ]);

  return (
    <>
      <div className="margin-top-4 margin-bottom-8">
        <span tabIndex="0" aria-label={modalSubTitle}>
          {modalSubTitle}
        </span>
        <div className="grid-row margin-top-4">
          <div className="grid-col-12">
            <MakeModelSummary
              make={make}
              modelName={modelName}
              modelCode={modelCode}
              nhtsaModelCode={nhtsaModelCode}
            />
          </div>
        </div>
        <div className="grid-row margin-top-4">
          <div className="grid-col-12">
            <div className="bg-primary-lighter padding-2 radius-md">
              <SelectMakeModelPanel />
            </div>
          </div>
        </div>
        <CommentSection
          commentBody={commentBody}
          onCommentChange={onCommentChange}
          errorMessage={commentError}
          required
        />
      </div>
      <div className="flex-align-start flex-justify-start">
        <Button
          data-testid="approve-reject-modal-cancel-btn"
          variant="unstyled"
          onClick={onClose}
          className="margin-right-2"
          label="Cancel"
        />
        <Button
          variant="primary"
          onClick={handleSave}
          label={loading ? 'Saving...' : 'Save and approve'}
        />
      </div>
    </>
  );
};

MakeModelEditContent.propTypes = {
  data: PropTypes.shape({
    make: PropTypes.object.isRequired,
    modelCode: PropTypes.string.isRequired,
    modelName: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  refetchData: PropTypes.func.isRequired,
};

export default MakeModelEditContent;
